import { useCallback, useEffect, useRef } from 'react';
import { Popper } from '@mui/material';
import { getPortalZoomScale } from 'components/ui/utilities/zoom';
import { SecondaryNavigationIconButton } from './SecondaryNavigation';
import { useSecondaryNavigationOverflow } from './SecondaryNavigation.overflowContext';
import type { SecondaryNavigationCollapsibleGroupProps } from './SecondaryNavigation.props';
import {
  SecondaryNavigationCollapsibleGroupFlyout,
  SecondaryNavigationCollapsibleGroupRoot,
} from './SecondaryNavigation.styles';
import { SECONDARY_NAVIGATION_TEST_IDS } from './SecondaryNavigation.testIds';

// Small delay before collapsing on mouse-out so moving from
// the icon to the flyout doesn't close it mid-transit
const CLOSE_DELAY_MS = 150;

// Renders its full content inline until the actions row runs out of space, then collapses behind an
// icon that expands the content over the rest of the nav on click and closes on mouse-out.
export const SecondaryNavigationCollapsibleGroup = ({
  ariaLabel,
  children,
  expandKey,
  icon,
}: SecondaryNavigationCollapsibleGroupProps) => {
  const { expandedGroupKey, isCompact, nestedMenuOpenCount, setExpandedGroupKey, zoomLevel } =
    useSecondaryNavigationOverflow();
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isExpanded = expandedGroupKey === expandKey;
  // Tracks whether the cursor is currently over the root or flyout so we know
  // whether to start the close timer when a nested portal menu closes.
  const isMouseOverRef = useRef(false);

  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelScheduledClose();
    // Don't start the timer while a child portal menu is open; the counter
    // dropping back to 0 will re-trigger via the effect below.
    if (nestedMenuOpenCount > 0) {
      return;
    }
    closeTimerRef.current = setTimeout(() => {
      // Only clear if this group is still the one expanded to prevent a stale timer
      // from switching groups quickly and wipe out a different group that opened since.
      setExpandedGroupKey(current => (current === expandKey ? null : current));
    }, CLOSE_DELAY_MS);
  }, [cancelScheduledClose, expandKey, nestedMenuOpenCount, setExpandedGroupKey]);

  useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

  // When all nested portal menus have closed, start the collapse timer only if
  // the cursor has already left the flyout — otherwise mouse-out will handle it.
  useEffect(() => {
    if (nestedMenuOpenCount === 0 && !isMouseOverRef.current) {
      scheduleClose();
    }
  // scheduleClose is intentionally omitted: it changes every render because
  // nestedMenuOpenCount is in its dep array. Adding it would create an infinite
  // loop (effect → scheduleClose → effect). The count itself is the real signal.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nestedMenuOpenCount]);

  // Produce a virtual element whose getBoundingClientRect compensates for the
  // CSS zoom applied to #root so the popper positions against unscaled viewport coords.
  const getZoomCorrectedAnchorEl = useCallback((): HTMLElement => {
    const el = anchorRef.current;
    if (!el) {
      // The Popper is only open when anchorRef is populated
      return document.body;
    }
    const scale = getPortalZoomScale(zoomLevel);

    return {
      getBoundingClientRect: () => {
        const rect = el.getBoundingClientRect();
        return {
          top: rect.top / scale,
          left: rect.left / scale,
          right: rect.right / scale,
          bottom: rect.bottom / scale,
          width: rect.width / scale,
          height: rect.height / scale,
          x: rect.x / scale,
          y: rect.y / scale,
          toJSON: () => rect.toJSON(),
        };
      },
    } as unknown as HTMLElement;
  }, [zoomLevel]);

  const handleMouseEnter = useCallback(() => {
    isMouseOverRef.current = true;
    cancelScheduledClose();
  }, [cancelScheduledClose]);

  const handleMouseLeave = useCallback(() => {
    isMouseOverRef.current = false;
    scheduleClose();
  }, [scheduleClose]);

  if (!isCompact) {
    // The actions row reliably overflows — and collapses — once it truly runs out of room.
    return <SecondaryNavigationCollapsibleGroupRoot>{children}</SecondaryNavigationCollapsibleGroupRoot>;
  }

  return (
    <SecondaryNavigationCollapsibleGroupRoot onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <span ref={anchorRef}>
        <SecondaryNavigationIconButton
          active={isExpanded}
          ariaLabel={ariaLabel}
          data-testid={SECONDARY_NAVIGATION_TEST_IDS.collapsibleGroupToggle(expandKey)}
          icon={icon}
          onClick={() => setExpandedGroupKey(isExpanded ? null : expandKey)}
          tooltipTitle={isExpanded ? '' : ariaLabel}
          variant="contained"
        />
      </span>
      <Popper
        anchorEl={getZoomCorrectedAnchorEl}
        modifiers={[{ name: 'preventOverflow', options: { boundary: 'viewport' } }]}
        open={isExpanded}
        placement="left"
        style={{ zIndex: 1300 }}>
        <SecondaryNavigationCollapsibleGroupFlyout onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {children}
        </SecondaryNavigationCollapsibleGroupFlyout>
      </Popper>
    </SecondaryNavigationCollapsibleGroupRoot>
  );
};

export default SecondaryNavigationCollapsibleGroup;
